import { ArrowLeftIcon } from "lucide-react";
import { ReactNode } from "react";
import { cn } from "cy-fe/src/utils/cn";
import { UnstyledButton } from "cy-fe";
import { useRouter } from "next/navigation";

/***
 * StepPageLayout is a layout component that provides a header, subheader and a back button.
 * It provides a consistent look and feel for steps in a wizard-like flow.
 */
export function StepPageLayout({
  children,
  subHeaderRender,
  headerRender,
  onBack,
  backUrl,
  className,
  "data-testid": dataTestId,
}: {
  children: ReactNode;
  subHeaderRender?: () => ReactNode;
  headerRender: () => ReactNode;
  onBack?: () => void;
  backUrl?: string;
  className?: string;
  "data-testid"?: string;
}) {
  const router = useRouter();
  const baseTestId = dataTestId || "step-page-layout";
  function backHandler() {
    if (onBack) {
      onBack();
    }
    if (backUrl) {
      router.push(backUrl);
    }
  }
  return (
    <>
      <div
        className="relative mx-auto flex max-w-[60rem] items-center justify-center"
        data-testid={`${baseTestId}-header`}
      >
        <div className="max-w-[66%]" data-testid={`${baseTestId}-title`}>
          {headerRender()}
        </div>
        {onBack || !!backUrl ? (
          <UnstyledButton
            className="absolute top-1 left-7 flex items-center rounded border-0 sm:left-10"
            onClick={backHandler}
            data-testid={`${baseTestId}-back-button`}
          >
            <ArrowLeftIcon className="mr-1 h-6 w-6 text-gray-700" />
            <span className="hidden font-semibold underline underline-offset-4 sm:inline">
              Back
            </span>
          </UnstyledButton>
        ) : null}{" "}
      </div>
      {!!subHeaderRender && (
        <div
          className="mt-1 text-center text-sm text-gray-700"
          data-testid={`${baseTestId}-subheader`}
        >
          {subHeaderRender()}
        </div>
      )}
      <div
        className={cn("mx-auto mt-6 pb-10", className)}
        data-testid={`${baseTestId}-content`}
      >
        {children}
      </div>
    </>
  );
}
